import { useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'

import StoryCarousel from '../components/StoryCarousel/StoryCarousel'
import ImageCaption from '../components/ImageCaption/ImageCaption'
import { fetchStory } from '../api/storiesApi'
import styles from './ArticleDetailPage.module.css'

function renderInline(md: string): string {
  return DOMPurify.sanitize(marked.parseInline(md) as string)
}

function renderBlock(md: string): string {
  return DOMPurify.sanitize(marked.parse(md) as string)
}

export default function ArticleDetailPage() {
  const { lang = 'en', city = '', clusterId = '' } = useParams<{
    lang: string
    city: string
    clusterId: string
  }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const id = Number(clusterId)
  const date = searchParams.get('date') || undefined
  const days = searchParams.get('days') ? Number(searchParams.get('days')) : 7

  const { data: story, isLoading, isError } = useQuery({
    queryKey: ['story', id, lang],
    queryFn: () => fetchStory(id, lang),
    enabled: !isNaN(id) && id > 0,
    staleTime: 5 * 60 * 1000,
  })

  const titleHtml = useMemo(
    () => renderInline(story?.ai_title ?? ''),
    [story?.ai_title],
  )

  const bodyHtml = useMemo(
    () => renderBlock(story?.ai_text ?? ''),
    [story?.ai_text],
  )

  const formattedDate = story?.story_date
    ? new Date(story.story_date).toLocaleDateString(lang, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null

  return (
    <div className={styles.page}>
      <button
        type="button"
        className={styles.backBtn}
        onClick={() => navigate(`/${lang}/${city}`)}
      >
        {t('back_to_home')}
      </button>

      {isLoading && (
        <div className={styles.skeleton}>
          <div className={styles.skeletonTitle} />
          <div className={styles.skeletonLine} />
          <div className={styles.skeletonLine} />
          <div className={styles.skeletonLineShort} />
        </div>
      )}

      {isError && (
        <p className={styles.error}>{t('error_loading')}</p>
      )}

      {story && (
        <>
          <article className={styles.article}>
            <div className={styles.meta}>
              <span className={styles.categoryBadge}>{story.category}</span>
              <span className={styles.rankBadge} title={t('significance')}>
                ★ {story.rank.toFixed(1)}
              </span>
            </div>

            {story.image_url && (
              <figure className={styles.floatFigure}>
                <img
                  src={story.image_url}
                  alt=""
                  className={styles.floatImage}
                />
                <ImageCaption />
              </figure>
            )}

            <h1
              className={styles.title}
              dangerouslySetInnerHTML={{ __html: titleHtml }}
            />

            {formattedDate && (
              <div className={styles.storyDate}>{formattedDate}</div>
            )}

            <div
              className={styles.body}
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          </article>

          <section className={styles.relatedSection}>
            <StoryCarousel
              criterion="similar"
              seedClusterId={story.cluster_id}
              lang={lang}
              citySlug={city}
              heading={t('related_articles')}
              days={days}
              date={date}
            />
          </section>
        </>
      )}
    </div>
  )
}
